/**
 * Normalize coordinates to start from 0
 * @param axis - Array of coordinate values
 * @returns Normalized array
 */
export const normalizeCoordinates = (axis: number[]): number[] => {
  const min = Math.min(...axis)
  return axis.map((value) => value - min)
}

/**
 * Calculate chart ranges and viewing parameters
 * @param xAxis - X-axis data
 * @param yAxis - Y-axis data
 * @param zMin - Minimum Z value
 * @param zMax - Maximum Z value
 * @returns Calculated ranges and viewing distance
 */
export const calculateChartRanges = (
  xAxis: number[],
  yAxis: number[],
  zMin: number,
  zMax: number
) => {
  const normalizedXAxis = normalizeCoordinates(xAxis)
  const normalizedYAxis = normalizeCoordinates(yAxis)

  const xRange =
    normalizedXAxis[normalizedXAxis.length - 1] - normalizedXAxis[0]
  const yRange =
    normalizedYAxis[normalizedYAxis.length - 1] - normalizedYAxis[0]
  const zRange = zMax - zMin

  const maxRange = Math.max(xRange, yRange, zRange)
  const viewDistance = maxRange * 2

  return {
    normalizedXAxis,
    normalizedYAxis,
    xRange,
    yRange,
    zRange,
    maxRange,
    viewDistance,
  }
}

/**
 * Create 3D axis configuration
 * @param type - Axis type ('category' or 'value')
 * @param name - Axis name
 * @param data - Axis data (for category type)
 * @param options - Additional options
 * @returns Axis configuration object
 */
export const create3DAxisConfig = (
  type: 'category' | 'value',
  name: string,
  data?: number[],
  options?: {
    min?: number
    max?: number
    formatter?: (value: any) => string
    interval?: number
  }
) => {
  const config: any = {
    type,
    name,
    nameTextStyle: { fontSize: 12 },
  }

  if (type === 'category' && data) {
    config.data = data
    config.axisLabel = {
      show: true,
      interval: options?.interval ?? Math.floor(data.length / 5),
      formatter:
        options?.formatter ?? ((value: any) => `${Number(value).toFixed(0)}m`),
    }
  } else if (type === 'value') {
    if (options?.min !== undefined) config.min = options.min
    if (options?.max !== undefined) config.max = options.max
    config.axisLabel = {
      formatter:
        options?.formatter ?? ((value: number) => `${value.toFixed(0)}m`),
    }
  }

  return config
}

/**
 * Create visual map configuration for charts
 * @param min - Minimum value
 * @param max - Maximum value
 * @param options - Additional options
 * @returns Visual map configuration
 */
export const createVisualMapConfig = (
  min: number,
  max: number,
  options?: {
    show?: boolean
    colors?: string[]
  }
) => {
  return {
    show: options?.show ?? true,
    min,
    max,
    inRange: {
      color: options?.colors ?? [
        '#2E7D32',
        '#66BB6A',
        '#81C784',
        '#FFF59D',
        '#FFB74D',
        '#FF8A65',
        '#EF5350',
        '#D32F2F',
      ],
    },
    text: [`${max.toFixed(1)}m`, `${min.toFixed(1)}m`],
    textStyle: { color: '#333', fontSize: 13, fontWeight: 500 },
    itemWidth: 20,
    itemHeight: 140,
    right: 10,
    top: 'center',
  }
}

/**
 * Create grid3D configuration
 * @param viewDistance - Viewing distance
 * @param boxDimensions - Box dimensions
 * @returns Grid3D configuration
 */
export const createGrid3DConfig = (
  viewDistance: number,
  boxDimensions: {
    width: number
    height: number
    depth: number
  }
) => {
  return {
    viewControl: {
      distance: viewDistance,
      alpha: 30,
      beta: 30,
      autoRotate: false,
    },
    light: {
      main: {
        intensity: 1.2,
        shadow: false,
      },
      ambient: {
        intensity: 0.5,
      },
    },
    axisPointer: {
      show: true,
    },
    boxWidth: boxDimensions.width,
    boxHeight: boxDimensions.height,
    boxDepth: boxDimensions.depth,
  }
}
